import Tab from '../TabBar/Tab/Tab';
import SidebarGamification from './SidebarGamification';
import { SidebarProps } from './sidebar.interface';
import { Bell, BookOpen, ClipboardList, Gamepad2, ScrollText } from 'lucide-react';
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Home from 'src/assets/images/icons/default/Home';
import LinkIcon from 'src/assets/images/icons/default/Link';
import MoneyBill from 'src/assets/images/icons/default/MoneyBill';
import User from 'src/assets/images/icons/default/UserWhite';
import Logo from 'src/assets/images/logo/Logo';
import { useOptionalNotificationInboxContext } from 'src/contexts/NotificationInboxContext/NotificationInboxContext';
import { useUserAccountDetailsContext } from 'src/contexts/UserAccountDetailsContext/UserAccountDetailsContext';
import { useInviteMenuNotifications } from 'src/hooks/use-invite-menu-notifications';
import { useTranslationByKey } from 'src/hooks/use-translation-by-key';

const SURVEY_TIPS_URL = 'https://rewardbloom.com/en/survey-tips';

/**
 * Sidebar component displays gamification container and navigation tabs.
 *
 * @param {SidebarProps} props - The props for the Sidebar component.
 * @param {HighlightState} [props.highlightState] - Highlight state.
 * @param {Function} [props.onHighlightStateChange] - Callback function triggered to change the highlight state.
 * @param {Function} [props.setActiveModal] - Callback function triggered to set the active modal.
 * @returns {JSX.Element} The rendered Sidebar component.
 */
const Sidebar: React.FC<SidebarProps> = ({
  showMyAccountTabWhileOnboarding = false,
  showGamification = true,
  gamification,
}) => {
  const t = useTranslationByKey('COMPONENTS.TAB_BAR');
  const { unreadGameInviteCount, unreadProjectInviteCount } = useInviteMenuNotifications();
  const { accountDetails } = useUserAccountDetailsContext();
  const unreadCount = useOptionalNotificationInboxContext()?.unreadCount ?? 0;

  const tabs = [
    { icon: <Home className="w-5 h-5" />, label: t('HOME'), path: '/home' },
    { icon: <ScrollText className="w-5 h-5" />, label: t('QUESTS'), path: '/quests' },
    { icon: <Gamepad2 className="w-5 h-5" />, label: t('GAMES'), path: '/games' },
    { icon: <ClipboardList className="w-5 h-5" />, label: t('SURVEYS'), path: '/surveys' },
    { icon: <MoneyBill className="w-5 h-5" />, label: t('REWARDS'), path: '/my-rewards' },
    { icon: <Bell className="w-5 h-5" />, label: t('INBOX'), path: '/notifications' },
    { icon: <LinkIcon className="w-5 h-5" />, label: t('AFFILIATE'), path: '/affiliate' },
    { icon: <User className="w-5 h-5" />, label: t('MY_ACCOUNT'), path: '/my-account' },
    { icon: <BookOpen className="w-5 h-5" />, label: t('SURVEY_TIPS'), path: SURVEY_TIPS_URL, isExternal: true },
  ];
  const location = useLocation();
  const [selectedTabIndex, setSelectedTabIndex] = useState(
    tabs.findIndex((tab) => !tab.isExternal && location.pathname.includes(tab.path))
  );
  const navigate = useNavigate();

  return (
    <>
      <div className="flex flex-1 flex-col h-full min-h-0 overflow-y-auto border-r border-violet-100 bg-violet-50/70 backdrop-blur-sm">
        {/* Brand header with clean styling */}
        <div className="h-[50px] shrink-0 border-b border-violet-100 px-4">
          <Link to="/home" aria-label="Reward Bloom Home" className="flex h-full items-center gap-2.5 group">
            <div className="relative flex-shrink-0">
              <Logo className="h-7 w-7 transition-transform duration-300 group-hover:scale-105" />
            </div>
            <span className="truncate bg-gradient-to-r from-violet-700 via-purple-600 to-indigo-600 bg-clip-text font-zillaSlab text-lg font-bold leading-none text-transparent transition-opacity duration-300 group-hover:opacity-80">
              Reward Bloom
            </span>
          </Link>
        </div>

        {showGamification && gamification && <SidebarGamification {...gamification} />}

        {/* Clean navigation menu */}
        <nav className="flex flex-col flex-1 gap-2 p-4">
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              showWhileOnboarding={tab.path === '/my-account' && showMyAccountTabWhileOnboarding}
              isSelected={index === selectedTabIndex}
              icon={tab.icon}
              ariaLabel={tab.label}
              label={tab.label}
              colorScheme="purple"
              notificationCount={
                tab.path === '/games'
                  ? unreadGameInviteCount
                  : tab.path === '/surveys'
                    ? unreadProjectInviteCount
                    : tab.path === '/notifications'
                      ? unreadCount
                      : tab.path === '/quests' && accountDetails?.questsNotification
                        ? 1
                        : 0
              }
              onClick={() => {
                if (tab.isExternal) {
                  const newWindow = window.open(tab.path, '_blank', 'noopener,noreferrer');
                  if (newWindow) {
                    newWindow.opener = null;
                  }
                  return;
                }

                setSelectedTabIndex(index);
                navigate(tab.path);
              }}
            />
          ))}

          {/* Bottom spacing for desktop */}
          <div className="flex-1 md:min-h-[2rem]" />
        </nav>
      </div>
    </>
  );
};

export default Sidebar;
